import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    ClipboardList,
    Users2,
    Settings2,
    BookOpen,
    LogOut,
    KeyRound,
    X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV, AUTH, GUIDE, PROFILE, SETTINGS_NAV, SETUP, IRC_S2, IRC_S1, IRC_S6 } from "@/constants/testIds";
import GuidePanel from "@/components/GuidePanel";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import api from "@/lib/api";

const GUIDE_INTRO_NOTICE = "guide_intro_v1";

const items = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, tid: NAV.dashboardLink, end: true },
    { to: "/pay-runs", label: "Pay Runs", icon: ClipboardList, tid: NAV.payrunsLink },
    { to: "/employees", label: "Employees", icon: Users2, tid: NAV.employeesLink },
];

export default function Sidebar({ onNavigate }) {
    const { user, setUser, logout } = useAuth();
    const navigate = useNavigate();
    const [guideOpen, setGuideOpen] = useState(false);
    const [pwOpen, setPwOpen] = useState(false);
    // Wrap the <NavLink> onClick so tapping an item in the mobile drawer
    // closes it after navigation. Desktop just gets a no-op — the layout
    // wrapper isn't listening.
    const handleNav = () => { if (typeof onNavigate === "function") onNavigate(); };

    const showFirstLoginPrompt =
        user && user.dismissed_notices && !user.dismissed_notices.includes(GUIDE_INTRO_NOTICE);

    const dismissPrompt = async () => {
        try {
            const { data } = await api.post("/auth/dismiss-notice", { notice_id: GUIDE_INTRO_NOTICE });
            setUser?.({ ...user, dismissed_notices: data.dismissed_notices });
        } catch {
            setUser?.({ ...user, dismissed_notices: [...(user.dismissed_notices || []), GUIDE_INTRO_NOTICE] });
        }
    };

    const openGuide = () => {
        setGuideOpen(true);
        if (showFirstLoginPrompt) dismissPrompt();
    };

    return (
        <>
        <aside
            data-testid={NAV.sidebar}
            className="w-[240px] shrink-0 min-h-screen text-neutral-200 flex flex-col overflow-y-auto"
            style={{ background: "var(--kp-black)" }}
        >
            <div className="px-5 pt-6 pb-8">
                <div data-testid={NAV.kpChip} className="inline-flex items-center">
                    <img
                        src="/kp-logo-dark.png"
                        alt="KP Industrial"
                        className="h-9 w-auto"
                        style={{ maxWidth: "200px" }}
                    />
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">
                    MSS (PNG) Payroll
                </div>
            </div>

            <nav className="px-3 flex-1">
                {items.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.end}
                        data-testid={it.tid}
                        onClick={handleNav}
                        className={({ isActive }) =>
                            [
                                "group flex items-center gap-3 px-3 py-2.5 mb-1 text-[13px] transition-colors",
                                "border-l-2",
                                isActive
                                    ? "text-white bg-[#141416]"
                                    : "text-neutral-400 border-transparent hover:text-white hover:bg-[#141416]",
                            ].join(" ")
                        }
                        style={({ isActive }) => ({
                            borderLeftColor: isActive ? "var(--kp-red)" : "transparent",
                        })}
                    >
                        <it.icon size={16} strokeWidth={1.75} />
                        <span>{it.label}</span>
                    </NavLink>
                ))}

                {/* Guide — every authenticated role. First-login discovery is a red dot on the button;
                    a popover has been tried in three positions and each conflicted with something the
                    sidebar or main content actually needs, so we've settled on the dot alone. */}
                <div className="relative mt-1">
                    <button
                        data-testid={GUIDE.openBtn}
                        onClick={openGuide}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] border-l-2 border-transparent text-neutral-400 hover:text-white hover:bg-[#141416] transition-colors"
                    >
                        <BookOpen size={16} strokeWidth={1.75} />
                        <span>Guide</span>
                        {showFirstLoginPrompt && (
                            <span
                                data-testid={GUIDE.prompt}
                                className="ml-auto inline-block w-2 h-2 rounded-full"
                                style={{ background: "var(--kp-red)" }}
                                aria-label="New — open the guide"
                                title="New — open the guide"
                            />
                        )}
                    </button>
                </div>

                {/* Settings — visibility is per-item, not per-section.
                    Payroll rules + Public holidays: readable by operators so
                    the bookkeeper can see which rules are confirmed and which
                    are still awaiting his answers (edits are admin-only, the
                    page renders a read-only chip when he opens it).
                    Employee setup + Users: admin-only — they write pay rates
                    and permissions. */}
                {(() => {
                    const isAdmin = user?.role === "admin";
                    const items = [
                        isAdmin && { to: "/setup/employees", label: "Employee setup", tid: SETUP.navLink },
                        isAdmin && { to: "/settings/users", label: "Users", tid: NAV.usersLink },
                        { to: "/settings/payroll", label: "Payroll rules", tid: SETTINGS_NAV.payrollLink },
                        { to: "/settings/holidays", label: "Public holidays", tid: SETTINGS_NAV.holidaysLink },
                        { to: "/reports/irc-s2", label: "IRC · Form S2", tid: IRC_S2.navLink },
                        { to: "/reports/irc-s1", label: "IRC · Form S1", tid: IRC_S1.navLink },
                        { to: "/reports/irc-s6", label: "IRC · Form S6", tid: IRC_S6.navLink },
                    ].filter(Boolean);
                    if (!items.length) return null;
                    return (
                        <>
                            <div className="mt-6 px-3 text-[10px] uppercase tracking-[0.2em] text-neutral-600 font-display">
                                Settings
                            </div>
                            {items.map((s) => (
                                <NavLink
                                    key={s.to}
                                    to={s.to}
                                    data-testid={s.tid}
                                    onClick={handleNav}
                                    className={({ isActive }) =>
                                        [
                                            "flex items-center gap-3 px-3 py-2.5 mt-1 text-[13px] border-l-2",
                                            isActive
                                                ? "text-white bg-[#141416]"
                                                : "text-neutral-400 border-transparent hover:text-white hover:bg-[#141416]",
                                        ].join(" ")
                                    }
                                    style={({ isActive }) => ({
                                        borderLeftColor: isActive ? "var(--kp-red)" : "transparent",
                                    })}
                                >
                                    <Settings2 size={16} strokeWidth={1.75} />
                                    <span>{s.label}</span>
                                </NavLink>
                            ))}
                        </>
                    );
                })()}
            </nav>

            <div className="p-4 border-t" style={{ borderColor: "var(--kp-line-dark)" }}>
                <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-600 mt-0.5 font-display flex items-center gap-1.5">
                    {user?.role}
                    {user?.password_is_seed && (
                        <span
                            data-testid={PROFILE.seedFlag}
                            title="You're still using a temporary password"
                            className="inline-block w-1.5 h-1.5 rounded-full"
                            style={{ background: "var(--kp-red)" }}
                        />
                    )}
                </div>
                <button
                    data-testid={PROFILE.changePasswordBtn}
                    onClick={() => setPwOpen(true)}
                    className="mt-3 flex items-center gap-2 text-[12px] text-neutral-400 hover:text-white"
                >
                    <KeyRound size={14} /> Change password
                </button>
                <button
                    data-testid={AUTH.logoutBtn}
                    onClick={async () => {
                        await logout();
                        navigate("/login", { replace: true });
                    }}
                    className="mt-2 flex items-center gap-2 text-[12px] text-neutral-400 hover:text-white"
                >
                    <LogOut size={14} /> Sign out
                </button>
            </div>
        </aside>
        <GuidePanel open={guideOpen} onClose={() => setGuideOpen(false)} />
        <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
        </>
    );
}
